import { useState } from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, Strategy, Lightning, UsersThree, CaretRight, ArrowRight, FilePdf } from "@phosphor-icons/react";
import MultiToolMark from "@/components/MultiToolMark";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const HUBS = {
  "use-cases": {
    label: "Use Cases",
    overline: "01 / Practitioner Pipeline",
    title: "Whiteboard → PDF, in one disciplined pass",
    body: [
      "MODUS is built for practitioner leaders running real alignment workshops — not slide decks. Walk a room through a framework live, capture every decision in a structured wizard, and the moment you finalise it becomes a clean, print-ready executive blueprint.",
      "No reformatting, no copy-paste into Word. The whiteboard-to-PDF pipeline means the artefact your team agreed on is the artefact you circulate — branded, dated and read-only.",
    ],
  },
  "socratic-ai": {
    label: "Socratic AI",
    overline: "02 / Ask Tzu",
    title: "An advisor that red-teams, not auto-writes",
    body: [
      "Most AI writes generic copy and lets you off the hook. Tzu does the opposite. It pressure-tests your reasoning step by step — surfacing weak assumptions, missing stakeholders and single points of failure — so the accountability for the decision stays with you.",
      "The result is sharper thinking, not outsourced thinking. Tzu protects executive judgement; it never replaces it.",
    ],
  },
  frameworks: {
    label: "Frameworks",
    overline: "03 / Methodology",
    title: "18+ academically-rigorous, non-proprietary models",
    body: [
      "Strategy, Operations and People — covered by battle-tested frameworks with real intellectual pedigree.",
    ],
    grid: [
      "FOCUS·ARMOR", "Eisenhower Matrix", "Playing to Win", "Porter's Five Forces",
      "VRIO", "SWOT", "LoNG PESTEL", "Ansoff Growth Matrix", "SIPOC", "DMAIC", "Theory of Constraints",
      "3C (Concern·Cause·Countermeasure)", "FMEA", "Asset Lifecycle", "Nadler-Tushman Congruence",
      "9-Box Talent & Succession", "Key-Person Dependency & Flight Risk", "RACI-E Accountability Grid",
    ],
    download: [
      { label: "MODUS Design Document (PDF)", href: "/MODUS-Design-Document.pdf" },
    ],
  },
  security: {
    label: "Security",
    overline: "04 / Data Commitments",
    title: "Your strategic data is your moat",
    body: [
      "Single-gate entry via Google SSO — no shared passwords, sessions encrypted and expiring in 7 days.",
      "Strict non-retention with our AI provider: every request to the model carries an explicit no-training, no-retention posture. Your inputs are never used to train models.",
      "A master kill-switch in your settings irreversibly erases your profile, authentication history and every document you own — total data sovereignty, on demand.",
    ],
  },
};

function HubLink({ onClick, label, testid }) {
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      className="group inline-flex items-center gap-1.5 font-mono text-[11px] tracking-[0.14em] uppercase text-slate-500 hover:text-slate-200 transition-colors"
    >
      {label}
      <ArrowRight size={11} className="opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all" />
    </button>
  );
}

export default function Login() {
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(null);

  const handleLogin = () => {
    setBusy(true);
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const hub = open ? HUBS[open] : null;

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2 font-['IBM_Plex_Sans',sans-serif]">
      {/* Left: brand panel */}
      <div className="relative hidden lg:flex flex-col justify-between bg-slate-900 p-12 overflow-hidden">
        <div
          className="absolute inset-0 opacity-20 bg-cover bg-center"
          style={{ backgroundImage: "url(https://images.unsplash.com/photo-1578776349090-de61da00ff1a?crop=entropy&cs=srgb&fm=jpg&q=85)" }}
        />
        <div className="absolute inset-0 bg-slate-900/85" />

        <div className="relative z-10 flex items-center gap-3 text-white">
          <MultiToolMark size={46} />
          <div className="leading-none">
            <span className="font-mono text-xl font-bold tracking-tight">MODUS</span>
            <p className="mt-1 text-[10px] tracking-[0.14em] uppercase text-slate-500">Your Leadership Multi-Tool</p>
          </div>
        </div>

        <div className="relative z-10 max-w-md">
          <p className="font-mono text-xs font-semibold tracking-[0.2em] uppercase text-indigo-400 mb-4">The Executive Operating Suite</p>
          <h1 className="text-4xl font-bold tracking-tight text-white leading-[1.1]">Make the hard calls.<br />Then make them happen.</h1>
          <p className="mt-5 text-slate-300 leading-relaxed">
            MODUS brings your decision, delivery and people tools into one disciplined workspace —
            organised across three leadership pillars so your operation aligns, decides, and executes with absolute clarity.
          </p>

          <div className="mt-8 space-y-3">
            <div className="flex items-start gap-3">
              <div className="w-9 h-9 rounded-sm bg-indigo-600/90 flex items-center justify-center shrink-0"><Strategy size={18} weight="duotone" className="text-white" /></div>
              <div>
                <p className="text-sm font-semibold text-white"><span className="font-mono text-indigo-300">STRATEGY</span> — decide what matters</p>
                <p className="text-sm text-slate-400 leading-snug">Prioritise ruthlessly and choose where to play.</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-9 h-9 rounded-sm bg-amber-600/90 flex items-center justify-center shrink-0"><Lightning size={18} weight="duotone" className="text-white" /></div>
              <div>
                <p className="text-sm font-semibold text-white"><span className="font-mono text-amber-300">OPERATIONS</span> — drive the execution</p>
                <p className="text-sm text-slate-400 leading-snug">Map processes, eliminate bottlenecks, and control risk.</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-9 h-9 rounded-sm bg-emerald-600/90 flex items-center justify-center shrink-0"><UsersThree size={18} weight="duotone" className="text-white" /></div>
              <div>
                <p className="text-sm font-semibold text-white"><span className="font-mono text-emerald-300">PEOPLE</span> — align &amp; develop teams</p>
                <p className="text-sm text-slate-400 leading-snug">Calibrate, delegate and coach with intent.</p>
              </div>
            </div>
          </div>

          <div className="mt-8 flex items-center gap-2 text-sm text-slate-300 border-t border-slate-700/60 pt-5">
            <CaretRight size={16} weight="bold" className="text-indigo-400 shrink-0" />
            <span>One workspace. The tools a leader needs to decide and deliver.</span>
          </div>
        </div>

        {/* Footer nav links → modal hubs */}
        <div className="relative z-10 space-y-4">
          <div className="flex flex-wrap items-center gap-x-6 gap-y-3" data-testid="hub-links">
            <HubLink testid="hub-link-use-cases" label="Use Cases" onClick={() => setOpen("use-cases")} />
            <HubLink testid="hub-link-socratic-ai" label="Socratic AI" onClick={() => setOpen("socratic-ai")} />
            <HubLink testid="hub-link-frameworks" label="Frameworks" onClick={() => setOpen("frameworks")} />
            <HubLink testid="hub-link-security" label="Security" onClick={() => setOpen("security")} />
            <Link data-testid="hub-link-pricing" to="/pricing" className="group inline-flex items-center gap-1.5 font-mono text-[11px] tracking-[0.14em] uppercase text-slate-500 hover:text-slate-200 transition-colors">
              Pricing
              <ArrowRight size={11} className="opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all" />
            </Link>
          </div>
          <div className="font-mono text-xs text-slate-600">Industrial-grade · MODUS Suite</div>
        </div>
      </div>

      {/* Right: login */}
      <div className="flex items-center justify-center bg-slate-50 px-6 py-12 min-h-screen">
        <div className="w-full max-w-sm mx-auto">
          <div className="lg:hidden flex items-center gap-3 mb-8 text-slate-900">
            <MultiToolMark size={42} onDark={false} />
            <div className="leading-none">
              <span className="font-mono text-lg font-bold">MODUS</span>
              <p className="mt-0.5 text-[10px] tracking-[0.14em] uppercase text-slate-400">Your Leadership Multi-Tool</p>
            </div>
          </div>
          <p className="text-xs font-semibold tracking-[0.1em] uppercase text-slate-500">Secure Access</p>
          <h2 className="mt-2 text-3xl font-bold tracking-tight text-slate-900">Sign in to continue</h2>
          <p className="mt-3 text-slate-500 leading-relaxed">Use your organisation Google account to access your MODUS workspace.</p>

          <button
            data-testid="google-login-button"
            onClick={handleLogin}
            disabled={busy}
            className="mt-8 w-full inline-flex items-center justify-center gap-3 rounded-sm border border-slate-900 bg-slate-900 px-4 py-3 text-sm font-medium text-white hover:bg-slate-800 hover:-translate-y-px transition-all duration-200 disabled:opacity-60"
          >
            <img src="https://www.google.com/favicon.ico" alt="" className="w-5 h-5 bg-white rounded-sm p-0.5" />
            {busy ? "Redirecting…" : "Access Workspace"}
          </button>

          <div className="mt-8 flex items-start gap-2 rounded-sm border border-slate-200 bg-white p-4">
            <ShieldCheck size={20} weight="duotone" className="text-emerald-600 shrink-0 mt-0.5" />
            <p className="text-sm text-slate-500">Sessions are encrypted and expire after 7 days. Your documents are private to your account.</p>
          </div>

          {/* mobile hub links */}
          <div className="mt-8 flex flex-wrap gap-x-5 gap-y-2 lg:hidden">
            <HubLink testid="hub-link-use-cases-m" label="Use Cases" onClick={() => setOpen("use-cases")} />
            <HubLink testid="hub-link-socratic-ai-m" label="Socratic AI" onClick={() => setOpen("socratic-ai")} />
            <HubLink testid="hub-link-frameworks-m" label="Frameworks" onClick={() => setOpen("frameworks")} />
            <HubLink testid="hub-link-security-m" label="Security" onClick={() => setOpen("security")} />
            <Link data-testid="hub-link-pricing-m" to="/pricing" className="group inline-flex items-center gap-1.5 font-mono text-[11px] tracking-[0.14em] uppercase text-slate-500 hover:text-slate-800 transition-colors">
              Pricing
              <ArrowRight size={11} className="opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all" />
            </Link>
          </div>
        </div>
      </div>

      <Dialog open={!!open} onOpenChange={(v) => !v && setOpen(null)}>
        {hub && (
          <DialogContent
            data-testid={`hub-modal-${open}`}
            className="sm:max-w-2xl bg-white border border-slate-200 rounded-none shadow-2xl p-0 overflow-hidden font-['IBM_Plex_Sans',sans-serif] gap-0"
          >
            <DialogHeader className="px-8 py-6 border-b border-slate-200 bg-slate-50 text-left space-y-1.5">
              <p className="font-mono text-[11px] font-semibold tracking-[0.2em] uppercase text-slate-500">{hub.overline}</p>
              <DialogTitle className="text-2xl font-medium tracking-tight text-slate-900">{hub.title}</DialogTitle>
            </DialogHeader>
            <div className="p-8 space-y-5 max-h-[60vh] overflow-y-auto">
              {hub.body.map((p, i) => (
                <p key={i} className="text-slate-600 leading-relaxed">{p}</p>
              ))}
              {hub.grid && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-px bg-slate-200 border border-slate-200">
                  {hub.grid.map((g) => (
                    <div key={g} className="bg-white px-4 py-3 text-sm font-medium text-slate-800">{g}</div>
                  ))}
                </div>
              )}
              {hub.download && (
                <div className="pt-1 space-y-2">
                  {hub.download.map((d) => (
                    <a
                      key={d.href}
                      href={d.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`hub-download-${d.href.replace(/[^a-z0-9]+/gi, "-").replace(/^-|-$/g, "").toLowerCase()}`}
                      className="group flex items-center gap-3 border border-slate-200 px-4 py-3 text-sm font-medium text-slate-800 hover:border-slate-400 transition-colors"
                    >
                      <FilePdf size={18} weight="duotone" className="text-indigo-600 shrink-0" />
                      <span className="flex-1">{d.label}</span>
                      <ArrowRight size={14} className="text-slate-400 group-hover:translate-x-0.5 transition-transform" />
                    </a>
                  ))}
                </div>
              )}
            </div>
          </DialogContent>
        )}
      </Dialog>
    </div>
  );
}
